import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { toast } from "sonner";

import { canWriteDeck } from "@/lib/deckSession";
import { forgetDiagramFile } from "@/lib/diagramSvg";
import {
  deleteAsset,
  deleteChartDocIfOwnedBy,
  deleteDiagramDocIfOwnedBy,
  deleteGanttDocIfOwnedBy,
  deleteGriddDocIfOwnedBy,
  putChartDoc,
  putDeck,
  putDiagramDoc,
  putGanttDoc,
  putGriddDoc,
  putNuggetSlide,
} from "@/lib/idb";
import { nuggetSlideFrom } from "@/lib/nugget";
import { ownerForContentDoc } from "@/store/ownerSelectors";

import type { AppDispatch, RootState } from "./index";
import {
  addSlide,
  deleteSlide,
  linkBroken,
  linkRevSynced,
  linkSet,
  moveSlide,
  nuggetApplied,
  purgeSlide,
  restoreSlide,
  setLogo,
  setSlideLevel,
  setStyleSheet,
  setTitle,
  setTransition,
  updateSlide,
} from "./documentSlice";
import {
  griddDocCreated,
  griddDocRemoved,
  griddDocUpdated,
} from "./griddSlice";
import {
  diagramDocCreated,
  diagramDocRemoved,
  diagramDocUpdated,
} from "./diagramSlice";
import {
  ganttDocCreated,
  ganttDocRemoved,
  ganttDocUpdated,
} from "./ganttSlice";
import {
  chartDocCreated,
  chartDocRemoved,
  chartDocUpdated,
} from "./chartSlice";

// Persistence side-effects. Listening for specific actions (rather than a
// store.subscribe blob) lets each effect write only the record that changed
// (TODO §4.2). `hydrated` is deliberately excluded — hydration must not trigger
// a redundant write-back.
export const persistenceListener = createListenerMiddleware();

const startAppListening = persistenceListener.startListening.withTypes<
  RootState,
  AppDispatch
>();

// === The single-writer gate (TODO §12.5.1) ===
//
// Every effect below asks this first. A page that does not hold the deck's Web Lock is a
// read-only viewer: its edits stay in memory and its banner says so. Gating HERE rather
// than in `idb.ts` is deliberate — deck-level operations issued from the documents home
// (create, duplicate, import, delete, rename) belong to no session and must stay allowed.
//
// One exception is worth naming: this is the ONLY thing standing between a demoted page
// and the deck. Take-over demotes the holder mid-session (§12.5.2), so the check has to
// run per action, not once at open.
function writable(state: RootState): boolean {
  return canWriteDeck(state.document.id);
}

// === The nugget projection (TODO §17.2.2) ===
//
// One edit to a linked slide has to reach every other deck that uses it. There is only ever
// one deck open per page (§12.1), so "edited everywhere at the same time" cannot mean live
// co-editing of five decks — it means the edit lands on the NUGGET the moment it is made, and
// every other deck picks it up on its next open, or immediately if it happens to be open in
// another window (`nugget-updated`, §17.2.4).
//
// Only the mirrored fields travel (§17.2.1). `rev` is stamped inside the IndexedDB
// transaction and comes back here to be recorded on the slide, which is what the reconcile on
// open compares against (§17.2.5).
//
// A `null` return means the write did not happen — no storage, or the nugget was deleted from
// the shelf while this deck was open. Neither is an error worth interrupting an edit for: the
// slide keeps every one of its fields and its dead link falls away on the next open. Telling
// the deck it is in step with a revision that does not exist would be the real damage.
async function projectSlideToNugget(
  api: { getState: () => RootState; dispatch: AppDispatch },
  slideId: string,
): Promise<void> {
  const slide = api.getState().document.slides.find((s) => s.id === slideId);
  const link = slide?.link;
  if (!slide || !link) return;
  const rev = await putNuggetSlide(link.nuggetId, nuggetSlideFrom(slide));
  if (rev === null) return;
  api.dispatch(linkRevSynced({ id: slideId, nuggetId: link.nuggetId, rev }));
}

// The deck is a single IndexedDB record, so any document change rewrites that one
// record. updatedAt is stamped here, not in the reducers — a Date in a reducer is
// impure and breaks time-travel debugging (TODO §7.4). Grid edits already arrive
// debounced 400ms from the iframe, so we write straight through with no second
// debounce (TODO §4.3).
startAppListening({
  matcher: isAnyOf(
    setTitle,
    setStyleSheet,
    setTransition,
    setLogo,
    addSlide,
    updateSlide,
    deleteSlide,
    restoreSlide,
    purgeSlide,
    moveSlide,
    setSlideLevel,
    // §17 Three of the four link reducers write to the deck record like any other slide
    // edit. They are rare — an insert, a break, another window's change — so the deck write
    // each one costs is not worth avoiding, and leaving them out would mean a link that is
    // real in memory and absent from storage until the next keystroke.
    //
    // **`linkRevSynced` is deliberately NOT here.** It is dispatched from exactly one place —
    // `projectSlideToNugget`, below, called from inside this effect — and the very next thing
    // that happens is this effect re-reading the document and writing it. Listing it would
    // make every keystroke on a linked slide two deck writes instead of one, the second
    // identical to the first.
    linkSet,
    linkBroken,
    nuggetApplied,
  ),
  effect: async (action, api) => {
    const state = api.getState();
    if (!writable(state)) return;
    // §17.2.2 The projection rides HERE, in front of the deck write rather than in a
    // listener of its own, and the ordering is the reason: it stamps a new nugget `rev`
    // which has to land on `slide.link.rev` BEFORE the deck record is written, or the deck
    // on disk claims to be one revision behind a nugget it is actually in step with. Two
    // separate listeners cannot express "before"; they interleave.
    if (updateSlide.match(action)) {
      await projectSlideToNugget(api, action.payload.id);
    }
    // Re-read: the projection above may have dispatched `linkRevSynced` into it.
    const deck = api.getState().document;
    const result = await putDeck({
      ...deck,
      meta: { ...deck.meta, updatedAt: new Date().toISOString() },
    });
    // "conflict" means another page wrote a newer revision of this deck — only reachable
    // where Web Locks is missing (§12.5.5), since the lock otherwise guarantees we are the
    // only writer. Retrying would be wrong: everything we hold is built on a base that no
    // longer exists, so the deck has to be re-read. Say so rather than losing the edit
    // silently, which is the exact failure §12 exists to end.
    if (result === "conflict") {
      toast.error("This deck changed in another window", {
        description: "Reload the page to pick up the newer version.",
        id: "deck-conflict",
      });
    }
  },
});

// Create and update carry the same payload and do the same write. Two actionCreator
// listeners (rather than one isAnyOf matcher) keep the payload type inferred.
//
// While a stylesheet-preview session is active (styleDraft.draft !== null), grid
// bodies are NOT persisted (TODO §8.2). A preview push re-bakes each cell's cached
// hex/pt values, which trips the embed's 400ms save and would otherwise write
// uncommitted styling to IndexedDB before the user ever hits Save. On Save/Cancel the
// draft clears and the subsequent re-bake (to the committed sheet) persists normally.
for (const actionCreator of [griddDocCreated, griddDocUpdated]) {
  startAppListening({
    actionCreator,
    effect: async (action, api) => {
      const state = api.getState();
      if (!writable(state)) return;
      if (state.styleDraft.draft !== null) return;
      // §17.2.3 Under the OWNER of the slide that holds this grid, not under the open deck.
      // Grid and image edits need no projection at all — they are already writing to the
      // nugget's own body — but they do need the right owner, and this line is the whole of
      // that. Filed under the deck instead, the body would fork the first time someone typed
      // in a linked slide, and four other decks would keep rendering the copy nobody edits.
      await putGriddDoc(
        ownerForContentDoc(state, action.payload.docId),
        action.payload.docId,
        action.payload.doc,
      );
    },
  });
}

// §17.6.2 The ownership gate, the same shape the diagram and gantt deletes already have.
// Unlike the unconditional `deleteGriddDoc` this replaced, the row goes only if THIS deck
// owns it: a nugget-owned body is shared by every deck that links the nugget, so a slide here
// dropping its window must unlink and nothing more — which is exactly what the Redux-side
// `griddDocRemoved` already did before this ran.
//
// This was the last of the four stores to get one. Diagrams and gantts were written with an
// owner key from their first commit (§21/§4.3.3); grids and assets carried a raw deck id and
// needed the v5 → v6 migration in `idb.ts` first, which is precisely the cost §4.3.3 spent one
// function call to avoid.
startAppListening({
  actionCreator: griddDocRemoved,
  effect: async (action, api) => {
    const state = api.getState();
    if (!writable(state)) return;
    await deleteGriddDocIfOwnedBy(action.payload.docId, state.document.id);
  },
});

// === Diagram bodies (TODO §21/§4.3.2) ===
//
// The same two listeners the grids have, with two deliberate differences.
//
// 1. The row is filed under an OWNER KEY, not a raw deck id (§4.3.3). §17 has now arrived
//    and that foresight paid exactly as advertised: the change was to what this listener is
//    TOLD — `ownerForContentDoc` in place of `deckOwner` — and not one row already in a
//    customer's database had to move. The grid store two listeners up needed a migration to
//    reach the same line.
//
// 2. There is NO stylesheet-draft guard. Grids skip persistence while a sheet preview is
//    live because a preview push re-bakes every cell and trips the embed's autosave
//    (§8.2); a diagram never re-bakes from the sheet at all (§9.1), so a draft cannot
//    produce a spurious diagram write and there is nothing to suppress.
for (const actionCreator of [diagramDocCreated, diagramDocUpdated]) {
  startAppListening({
    actionCreator,
    effect: async (action, api) => {
      const state = api.getState();
      if (!writable(state)) return;
      // §17.2.3 Resolved from the slide holding this scene — `deckOwner(state.document.id)`
      // until §17, which was the same value for every row because a nugget could not own one.
      await putDiagramDoc(
        ownerForContentDoc(state, action.payload.docId),
        action.payload.docId,
        action.payload.doc,
      );
    },
  });
}

// §4.3.4 / §17.6.2 The ownership gate. Unlike `deleteGriddDoc`, this delete is CONDITIONAL:
// the row goes only if this deck owns it. A nugget-owned body is shared by every deck that
// links the nugget, so a slide here dropping its window must unlink and nothing more —
// which is exactly what the Redux-side `diagramDocRemoved` already did before this ran.
//
// §4.4.2 / §16.3 The images INSIDE a diagram go with it. They are ordinary `assets` rows, so
// nothing else would ever collect them: `dropContentArtifacts` deletes the blob behind an
// IMAGE window by walking the slide's contents, and a diagram's images are in none of them.
// Left behind they are quota the user cannot see, let alone reclaim — the orphan rule, in the
// one place that knows both which files the scene held and whether the body was really
// deleted.
//
// `getOriginalState()` is what makes this possible at all: by the time this effect runs the
// reducer has already removed the body from `diagram`, so the fileIds are only still readable
// in the pre-action state.
startAppListening({
  actionCreator: diagramDocRemoved,
  effect: async (action, api) => {
    const state = api.getState();
    if (!writable(state)) return;
    const removed = api.getOriginalState().diagram[action.payload.docId];
    const deleted = await deleteDiagramDocIfOwnedBy(
      action.payload.docId,
      state.document.id,
    );
    // Only when the BODY actually went. A nugget-owned body survives this deck dropping the
    // slide, and its images have to survive with it or the other four decks render holes.
    if (!deleted || !removed) return;
    for (const assetId of removed.fileIds ?? []) {
      await deleteAsset(assetId);
      forgetDiagramFile(assetId);
    }
  },
});

// === Gantt bodies (TODO §26/§3.3) ===
//
// The diagram pair above, repeated: owner key on the write, ownership gate on the delete,
// and no stylesheet-draft guard because a gantt does not re-bake from the sheet either
// (§26/§6). The one thing it does NOT need is the diagram delete's asset sweep — a schedule
// holds no blobs, so unlinking the row is the whole of the cleanup.
for (const actionCreator of [ganttDocCreated, ganttDocUpdated]) {
  startAppListening({
    actionCreator,
    effect: async (action, api) => {
      const state = api.getState();
      if (!writable(state)) return;
      // §17.2.3 Resolved from the slide, exactly as the diagram write above is.
      await putGanttDoc(
        ownerForContentDoc(state, action.payload.docId),
        action.payload.docId,
        action.payload.doc,
      );
    },
  });
}

startAppListening({
  actionCreator: ganttDocRemoved,
  effect: async (action, api) => {
    const state = api.getState();
    if (!writable(state)) return;
    await deleteGanttDocIfOwnedBy(action.payload.docId, state.document.id);
  },
});

// === Chart bodies (TODO §15/§3.3) ===
//
// The gantt pair with ONE addition, and it is the only thing in this file that debounces.
//
// **§3.3.3 — why the debounce is here and not in the reducer.** Every other body arrives
// pre-debounced from the library that emits it: the Gridd embed batches at ~400ms upstream, so
// does Excalidraw, and SVAR the same. A chart's data table is OURS, and it dispatches
// `chartDocUpdated` on every keystroke — which is what makes the chart redraw as you type, and
// exactly what must not become one IndexedDB write per character. Debouncing in the REDUCER
// would fix the writes by breaking the redraw; debouncing here leaves Redux instant and
// coalesces only the storage.
//
// **The pending set is what makes the coalescing safe, and it is not decoration.**
// `cancelActiveListeners()` cancels every other in-flight run of the same listener — it knows
// nothing about docIds — so with two charts on a slide, typing in the second would cancel the
// first's pending write and the first chart's last edits would never reach storage. So each
// run records its docId and the surviving run flushes the WHOLE set: whoever wins the race
// writes everyone's work, and a cancelled run losing its own turn costs nothing.
//
// Each doc is read from state at FLUSH time, never from the action payload. A cancelled run's
// payload is stale by definition, and the survivor's is one keystroke behind if another edit
// lands inside the delay; `getState()` after the delay is the last thing typed.
//
// No stylesheet-draft guard, for the reason the diagram and gantt writes have none: a chart
// resolves its look from the sheet at RENDER time and bakes nothing into the doc (§6.2), so a
// live preview cannot produce a spurious write. An edit made during a preview is a real edit.
const CHART_WRITE_DEBOUNCE_MS = 300;
const pendingChartWrites = new Set<string>();

for (const actionCreator of [chartDocCreated, chartDocUpdated]) {
  startAppListening({
    actionCreator,
    effect: async (action, api) => {
      if (!writable(api.getState())) return;
      pendingChartWrites.add(action.payload.docId);
      api.cancelActiveListeners();
      try {
        await api.delay(CHART_WRITE_DEBOUNCE_MS);
      } catch {
        // A newer edit superseded this run; its flush carries our docId too.
        return;
      }
      const state = api.getState();
      if (!writable(state)) return;
      // Drain before awaiting, so an edit arriving mid-flush queues a fresh run rather than
      // being swallowed by this one's iteration.
      const docIds = [...pendingChartWrites];
      pendingChartWrites.clear();
      for (const docId of docIds) {
        const doc = state.chart[docId];
        // Created and removed inside the debounce window: nothing to write, and the remove
        // listener below has already dealt with the row.
        if (!doc) continue;
        // §17.2.3 Resolved from the slide holding this chart, exactly as the three writes
        // above resolve theirs.
        await putChartDoc(ownerForContentDoc(state, docId), docId, doc);
      }
    },
  });
}

// Not debounced, deliberately: a delete is one event, never part of a burst, and making Clear
// Slide wait 300ms to reach storage buys nothing. Dropping the docId from the pending set is
// the half that matters — without it a flush queued a moment earlier would re-create the row
// this just deleted.
startAppListening({
  actionCreator: chartDocRemoved,
  effect: async (action, api) => {
    const state = api.getState();
    pendingChartWrites.delete(action.payload.docId);
    if (!writable(state)) return;
    await deleteChartDocIfOwnedBy(action.payload.docId, state.document.id);
  },
});
